import { designSystemColorPalettes, SharedUiColorNameV2 } from '@/shared/ui/theming';
import { type IconName, IconStyle, UiIconComponent } from '@/ui/icon/ui-icon';
import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    Renderer2,
    RendererStyleFlags2
} from '@angular/core';
import { AdvizeoUiElementImportance, AdvizeoUiElementSize } from '../../interfaces/AdvizeoUi.interface';

const sizeClassSuffix = '-icon-button-size';

@Component({
    styleUrls: ['./ui-icon-button.scss'],
    selector: 'ui-icon-button',
    templateUrl: 'ui-icon-button.html',
    imports: [NgClass, UiIconComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[class.ui-icon-button-disabled]': 'disabled()'
    }
})
export class UiIconButtonComponent {
    private _element = inject(ElementRef);
    private _renderer = inject(Renderer2);

    onSolidBackground = input(false);

    color = input<SharedUiColorNameV2>(SharedUiColorNameV2.DEFAULT);
    colorPalette = computed(() => designSystemColorPalettes[this.color()]);
    importance = input<AdvizeoUiElementImportance>(AdvizeoUiElementImportance.MEDIUM);

    /**
     * Inputs to directly set the icon color using the palette.
     * Use this for really specific use cases or to create other reusable ui component.
     * Be careful as the cost of maintenance will be higher if you use this inputs.
     */
    rawIconColor = input<string>();

    disabled = input(false);

    iconName = input.required<IconName>();
    // regular icons are too thin for buttons usually
    iconStyle = input<Omit<IconStyle, typeof IconStyle.REGULAR>>(IconStyle.BOLD);
    size = input<Extract<AdvizeoUiElementSize, 'medium' | 'small'>>(AdvizeoUiElementSize.MEDIUM);
    boundaries = input<'button' | 'icon'>('button');

    sizeClass = computed(() => ({ [this.size() + sizeClassSuffix]: true }));

    constructor() {
        effect(() => {
            for (const [variant, color] of Object.entries(this.colorPalette())) {
                this._renderer.setStyle(
                    this._element.nativeElement,
                    `--palette-${variant}-solid`,
                    color.solid,
                    RendererStyleFlags2.DashCase
                );
                this._renderer.setStyle(
                    this._element.nativeElement,
                    `--palette-${variant}-alpha`,
                    color.alpha,
                    RendererStyleFlags2.DashCase
                );
            }
        });
    }
}
